import { createContext, useContext, useState, useRef, useMemo } from 'react';

const EditorStateContext = createContext(null);

export const useEditorState = () => {
  const context = useContext(EditorStateContext);
  if (!context) {
    throw new Error('useEditorState must be used within EditorStateProvider');
  }
  return context;
};

export const EditorStateProvider = ({ children }) => {
  // Core editor state
  const [currentFile, setCurrentFile] = useState(null);
  const [currentFileId, setCurrentFileId] = useState(null);
  const [openFiles, setOpenFiles] = useState([]);
  const [currentTemplate, setCurrentTemplate] = useState(null);

  // Content loading state
  const [isContentLoading, setIsContentLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isTabSwitching, setIsTabSwitching] = useState(false);

  // Editor refs
  const editorRef = useRef(null);
  const loadedContentRef = useRef(null);
  const autoSaveTimeout = useRef(null);

  // Actions - memoize to prevent recreating on every render
  const actions = useMemo(() => ({
    // File operations
    setCurrentFile: (file) => {
      setCurrentFile(file);
      if (file) {
        setCurrentFileId(file.id);
        localStorage.setItem('lastOpenedFileId', file.id);
      }
    },

    setCurrentFileId: (fileId) => {
      setCurrentFileId(fileId);
      if (fileId) {
        localStorage.setItem('lastOpenedFileId', fileId);
      }
    },

    addOpenFile: (file) => {
      setOpenFiles(prev => {
        const exists = prev.find(f => f.id === file.id);
        if (exists) {
          return prev.map(f => f.id === file.id ? file : f);
        }
        return [...prev, file];
      });
    },

    removeOpenFile: (fileId) => {
      setOpenFiles(prev => prev.filter(f => f.id !== fileId));
    },

    updateOpenFile: (fileId, updates) => {
      setOpenFiles(prev => prev.map(f =>
        f.id === fileId ? { ...f, ...updates } : f
      ));
    },

    clearOpenFiles: () => {
      setOpenFiles([]);
    },

    setOpenFiles: (files) => {
      setOpenFiles(files);
    },

    // Template operations
    setCurrentTemplate: (template) => {
      setCurrentTemplate(template);
    },

    // Loading state
    setIsContentLoading: (loading) => {
      setIsContentLoading(loading);
    },

    setHasUnsavedChanges: (hasChanges) => {
      setHasUnsavedChanges(hasChanges);
    },

    setIsTabSwitching: (switching) => {
      setIsTabSwitching(switching);
    },

    // Editor ref management
    setEditorRef: (ref) => {
      editorRef.current = ref;
    },

    setLoadedContentRef: (fileId) => {
      loadedContentRef.current = fileId;
    },

    clearLoadedContentRef: () => {
      loadedContentRef.current = null;
    },

    // Autosave management
    scheduleAutoSave: (callback, delay = 2000) => {
      if (autoSaveTimeout.current) {
        clearTimeout(autoSaveTimeout.current);
      }
      autoSaveTimeout.current = setTimeout(callback, delay);
    },

    cancelAutoSave: () => {
      if (autoSaveTimeout.current) {
        clearTimeout(autoSaveTimeout.current);
        autoSaveTimeout.current = null;
      }
    }
  }), []); // Empty dependencies - actions don't depend on state

  const value = useMemo(() => ({
    // State
    state: {
      currentFile,
      currentFileId,
      openFiles,
      currentTemplate,
      isContentLoading,
      hasUnsavedChanges,
      isTabSwitching,
      editorRef,
      loadedContentRef,
      autoSaveTimeout
    },
    // Actions
    actions
  }), [
    currentFile,
    currentFileId,
    openFiles,
    currentTemplate,
    isContentLoading,
    hasUnsavedChanges,
    isTabSwitching,
    actions
  ]);

  return (
    <EditorStateContext.Provider value={value}>
      {children}
    </EditorStateContext.Provider>
  );
};
